import React, { useEffect, useRef } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Button,
    Grid,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { parseDate, formatDate } from 'common/utils/dates';

const ScrollableContent = styled('div')`
    flex-grow: 1;
    overflow: auto;
    min-height: 0;
`;

const Comment = styled('div')`
    display: flex;
    margin-bottom: 15px;

    picture {
        align-items: center;
        border-radius: 100%;
        display: flex;
        height: 35px;
        overflow: hidden;
        width: 35px;

        img {
            width: 100%;
        }
    }
`;

const Content = styled('div')`
    padding: 0 16px;
    flex-grow: 1;
`;

const MessagesList = ({ messages }) => {
    const { t } = useTranslation();
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollTo(
                0,
                messagesEndRef.current.scrollHeight,
            );
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const renderDate = (comment) => {
        if (comment.created_at) {
            return formatDate(
                parseDate(comment.created_at, 'dd/MM/yyyy HH:mm:ss'),
                'dd/MM/yyyy hh:mm:ss',
            );
        }
        return comment.created_at || '';
    };

    return (
        <ScrollableContent ref={messagesEndRef}>
            {messages.map((comment) => (
                <Comment key={`comment-${comment.id}`}>
                    <Avatar src={comment.image} />
                    <Content>
                        <div className="role-name">
                            <Grid container>
                                <Grid size={{ xs: 'grow' }}>
                                    <Typography variant="h6">
                                        {comment.username} -{' '}
                                        <small>
                                            ({comment.name} - {comment.role})
                                        </small>
                                    </Typography>
                                </Grid>
                                <Grid>
                                    <small>{renderDate(comment)}</small>
                                </Grid>
                            </Grid>
                        </div>
                        <Box>
                            {comment.isReject && (
                                <>
                                    <div className="title">
                                        <Button as="span" color="secondary">
                                            <CloseIcon />
                                        </Button>{' '}
                                        {t('Reason for rejection')}
                                    </div>
                                    {comment.reasons.map((item, index) => (
                                        <Button
                                            as="div"
                                            key={`reject-reason-${index}`}
                                        >
                                            {item}
                                        </Button>
                                    ))}
                                </>
                            )}
                            <div>{comment.message}</div>
                        </Box>
                    </Content>
                </Comment>
            ))}
        </ScrollableContent>
    );
};

MessagesList.propTypes = {
    messages: PropTypes.array.isRequired,
};

export default MessagesList;
